// @flow
import React, { useState, useEffect } from 'react';
import './MyAccountPassword.css';
// Components
import {
  validationHelpers,
  InputFieldErrorMessage,
  FormButton,
  PageLoader,
} from '../../../common';
import { Link } from 'react-router-dom';
// assets
import close_icon from '../../images/close-icon.svg';

type Props = {
  updatingLoading: boolean,
  error: boolean,
  onSubmit: (
    oldPassword: string,
    newPassword: string,
    newPasswordConfirmation: string,
  ) => void,
  onClose: () => void,
};

const PasswordStrengthTextAndColor = ({
  passwordStrength,
}: {
  passwordStrength: string,
}) => {
  const strengthMap = {
    strong: { text: 'Forte', className: 'account-password-strength-strong' },
    average: { text: 'Média', className: 'account-password-strength-medium' },
    weak: { text: 'Fraca', className: 'account-password-strength-poor' },
  };

  const currentStrength = strengthMap[passwordStrength];

  if (!currentStrength) return null;

  return (
    <span className="account-password-strength-text">
      Força da senha:{' '}
      <span className={currentStrength.className}>
        {' '}
        {currentStrength.text}{' '}
      </span>
    </span>
  );
};

const checkPasswordStrength = (password: string): string => {
  if (!password) return '';
  if (password.length < 8) return 'weak';

  let strengthScore = 0;
  if (/[a-z]/.test(password)) strengthScore += 1;
  if (/[A-Z]/.test(password)) strengthScore += 1;
  if (/[0-9]/.test(password)) strengthScore += 1;
  if (/[^a-zA-Z0-9]/.test(password)) strengthScore += 1;

  if (strengthScore === 1) return 'weak';
  if (strengthScore <= 3) return password.length >= 12 ? 'strong' : 'average';

  return 'strong';
};

const MyAccountPassword = (props: Props) => {
  const [oldPassword, setOldPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] =
    useState<string>('');

  const [badOldPassword, setBadOldPassword] = useState<string>('');
  const [badPassword, setBadPassword] = useState<string>('');
  const [passwordConfirmationError, setPasswordConfirmationError] =
    useState<string>('');

  const [serverError, setServerError] = useState<boolean>(false);

  const passwordStrength = checkPasswordStrength(newPassword);

  useEffect(() => {
    if (props.error && !props.updatingLoading) {
      setServerError(true);
    }
  }, [props.error, props.updatingLoading]);

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    let isValid = true;

    setBadOldPassword('');
    setBadPassword('');
    setPasswordConfirmationError('');
    setServerError(false);

    const oldPasswordResult = validationHelpers.user.password(oldPassword);
    if (oldPasswordResult) {
      setBadOldPassword(oldPasswordResult);
      isValid = false;
    }

    let newPasswordResult = validationHelpers.user.password(newPassword);

    if (!newPasswordResult && passwordStrength === 'weak') {
      newPasswordResult =
        'A senha escolhida é muito fraca. Tente misturar letras e números.';
    }

    if (newPasswordResult) {
      setBadPassword(newPasswordResult);
      isValid = false;
    }

    const confError = validationHelpers.user.passwordConfirmation(
      newPassword,
      newPasswordConfirmation,
    );
    if (confError) {
      setPasswordConfirmationError(confError);
      isValid = false;
    }

    if (isValid) {
      props.onSubmit(oldPassword, newPassword, newPasswordConfirmation);
    }
  };

  if (props.updatingLoading) {
    return <PageLoader />;
  }

  const displayOldPasswordError =
    badOldPassword || (serverError ? 'A senha atual está incorreta.' : '');

  return (
    <div className="my-account-password-main">
      <div className="my-account-password-header">
        <h3>Trocar Senha</h3>
        <button
          className="my-account-password-close-button"
          onClick={props.onClose}
        >
          <img src={close_icon} alt="Icone Para Fechar" />
        </button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="my-account-password-form">
          <InputFieldErrorMessage message={displayOldPasswordError} />
          <input
            name="oldPassword"
            type="password"
            className="my-account-password-large-input"
            placeholder="Digite sua senha atual"
            value={oldPassword}
            onChange={e => {
              setOldPassword(e.target.value);
              if (badOldPassword) setBadOldPassword('');
              if (serverError) setServerError(false);
            }}
          />

          <InputFieldErrorMessage message={badPassword} />
          <input
            name="newPassword"
            type="password"
            className="my-account-password-large-input"
            placeholder="Digite sua nova senha"
            value={newPassword}
            onChange={e => {
              setNewPassword(e.target.value);
              if (badPassword) setBadPassword('');
            }}
          />

          <InputFieldErrorMessage message={passwordConfirmationError} />
          <input
            name="newPasswordConfirmation"
            type="password"
            className="my-account-password-large-input"
            placeholder="Digite a senha novamente"
            value={newPasswordConfirmation}
            onChange={e => {
              setNewPasswordConfirmation(e.target.value);
              if (passwordConfirmationError) setPasswordConfirmationError('');
            }}
          />

          {passwordStrength !== '' && (
            <PasswordStrengthTextAndColor passwordStrength={passwordStrength} />
          )}
        </div>
        <div className="my-account-password-submit-content">
          <div className="my-account-password-forgot-password">
            <Link to="/esqueci-senha/">Esqueci a senha</Link>
          </div>
          <div className="my-account-password-submit-button">
            <FormButton
              text={'Salvar senha'}
              icon=""
              buttonType={'form-button-quarternary'}
              loading={props.updatingLoading}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default MyAccountPassword;
